// src/tiktactoe/TikTokToe.js
import React, { useState, useEffect } from 'react';
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import styles from "../styles/TikTokToe.module.css";

// Gift types with their costs and special functions
const GIFTS = {
  ROSE: { id: 1, name: "Rose", cost: 0, emoji: "🌹", special: null }, // Free for owner
  TIKTOK: { id: 2, name: "TikTok", cost: 5, emoji: "🎵", special: "double_move" },
  CORGI: { id: 3, name: "Corgi", cost: 10, emoji: "🐶", special: "block_opponent" },
  PLANE: { id: 4, name: "Plane", cost: 50, emoji: "✈️", special: "clear_row" },
  GALAXY: { id: 5, name: "Galaxy", cost: 100, emoji: "🌌", special: "reset_board" },
  UNICORN: { id: 6, name: "Unicorn", cost: 200, emoji: "🦄", special: "instant_win" }
};

const TikTokToe = () => {
  const [board, setBoard] = useState(Array(9).fill(null));
  const [isXNext, setIsXNext] = useState(true);
  const [playerGifts, setPlayerGifts] = useState({ X: 999, O: 999 }); // Unlimited gifts for testing
  const [winner, setWinner] = useState(null);
  const [selectedGift, setSelectedGift] = useState(GIFTS.ROSE);
  const [gameHistory, setGameHistory] = useState([]);
  const [specialActive, setSpecialActive] = useState(null);
  const [gameStarted, setGameStarted] = useState(false);
  const [showWinnerModal, setShowWinnerModal] = useState(false);

  // Calculate winner
  useEffect(() => {
    const lines = [
      [0, 1, 2], [3, 4, 5], [6, 7, 8], // rows
      [0, 3, 6], [1, 4, 7], [2, 5, 8], // columns
      [0, 4, 8], [2, 4, 6]             // diagonals
    ];
    
    for (let i = 0; i < lines.length; i++) {
      const [a, b, c] = lines[i];
      if (board[a] && board[a] === board[b] && board[a] === board[c]) {
        setWinner(board[a]);
        setShowWinnerModal(true);
        return;
      }
    }
    
    if (!board.includes(null)) {
      setWinner('Draw');
      setShowWinnerModal(true);
    }
  }, [board]);

  // Handle special gift functions
  const applySpecialEffect = (gift, index, currentPlayer) => {
    switch(gift.special) {
      case "double_move":
        // Player gets another move
        setIsXNext(isXNext);
        setSpecialActive(`Player ${currentPlayer} gets Double Move!`);
        setTimeout(() => setSpecialActive(null), 2000);
        break;
        
      case "block_opponent":
        // Block a random opponent square
        const emptySquares = board
          .map((square, idx) => square === null ? idx : null)
          .filter(val => val !== null);
        
        if (emptySquares.length > 0) {
          const randomIndex = emptySquares[Math.floor(Math.random() * emptySquares.length)];
          const newBoard = [...board];
          newBoard[randomIndex] = "BLOCKED";
          setBoard(newBoard);
          setSpecialActive(`Player ${currentPlayer} blocked a square!`);
          setTimeout(() => setSpecialActive(null), 2000);
        }
        break;
        
      case "clear_row":
        // Clear a random row
        const rows = [[0,1,2], [3,4,5], [6,7,8]];
        const randomRow = rows[Math.floor(Math.random() * rows.length)];
        const newBoard = [...board];
        randomRow.forEach(i => {
          if (newBoard[i] !== "X" && newBoard[i] !== "O") newBoard[i] = null;
        });
        setBoard(newBoard);
        setSpecialActive(`Player ${currentPlayer} cleared a row!`);
        setTimeout(() => setSpecialActive(null), 2000);
        break;
        
      case "reset_board":
        // Reset the board but keep scores
        setBoard(Array(9).fill(null));
        setSpecialActive(`Player ${currentPlayer} reset the board!`);
        setTimeout(() => setSpecialActive(null), 2000);
        break;
        
      case "instant_win":
        // Instant win condition
        setWinner(currentPlayer);
        setShowWinnerModal(true);
        setSpecialActive(`Player ${currentPlayer} used Instant Win!`);
        break;
        
      default:
        break;
    }
  };

  const handleClick = (index) => {
    if (!gameStarted || winner || board[index] || board[index] === "BLOCKED") return;
    
    const currentPlayer = isXNext ? 'X' : 'O';
    const isOwnerMove = currentPlayer === 'X'; // Owner is always X
    
    // Check if player has enough gifts to make a move (only for special gifts, Rose is free for owner)
    const giftCost = isOwnerMove && selectedGift.id === GIFTS.ROSE.id ? 0 : selectedGift.cost;
    
    if (playerGifts[currentPlayer] < giftCost) {
      alert(`Not enough gifts! You need ${giftCost} to make a move with ${selectedGift.name}`);
      return;
    }
    
    // Make the move
    const newBoard = [...board];
    newBoard[index] = currentPlayer;
    setBoard(newBoard);
    
    // Deduct gift cost (only for special gifts, Rose is free for owner)
    if (giftCost > 0) {
      setPlayerGifts(prev => ({
        ...prev,
        [currentPlayer]: prev[currentPlayer] - giftCost
      }));
    }
    
    // Add to game history
    setGameHistory(prev => [...prev, {
      player: currentPlayer,
      position: index,
      gift: selectedGift,
      timestamp: new Date(),
      message: `Player ${currentPlayer} used ${selectedGift.emoji} at position ${index + 1}`
    }]);
    
    // Apply special effect if any
    if (selectedGift.special) {
      applySpecialEffect(selectedGift, index, currentPlayer);
    } else {
      setIsXNext(!isXNext);
    }
  };

  // Owner sends a gift to the other player
  const sendGiftToOpponent = (giftType) => {
    if (!gameStarted) return;
    
    const opponent = isXNext ? 'O' : 'X'; // Send to the opponent
    
    // Check if owner has enough gifts to send
    if (playerGifts.X < giftType.cost) {
      alert(`Not enough gifts! You need ${giftType.cost} to send ${giftType.name}`);
      return;
    }
    
    // Deduct from owner and add to opponent
    setPlayerGifts(prev => ({
      ...prev,
      X: prev.X - giftType.cost,
      [opponent]: prev[opponent] + giftType.cost
    }));
    
    // Add to game history
    setGameHistory(prev => [...prev, {
      player: "OWNER",
      gift: giftType,
      timestamp: new Date(),
      message: `Owner sent ${giftType.emoji} to Player ${opponent}`
    }]);
  };

  // Simulate receiving a gift from a random viewer to a random player
  const simulateRandomGift = () => {
    if (!gameStarted) return;
    
    const giftTypes = Object.values(GIFTS);
    const randomGift = giftTypes[Math.floor(Math.random() * giftTypes.length)];
    const randomPlayer = Math.random() > 0.5 ? 'X' : 'O';
    const viewerNames = ['TikTokFan123', 'GiftGiver99', 'LiveViewer42', 'StreamSupporter', 'GameLover77'];
    const randomViewer = viewerNames[Math.floor(Math.random() * viewerNames.length)];
    
    // Add to player's gift count
    setPlayerGifts(prev => ({
      ...prev,
      [randomPlayer]: prev[randomPlayer] + 1
    }));
    
    // Add to game history
    setGameHistory(prev => [...prev, {
      player: "VIEWER",
      gift: randomGift,
      timestamp: new Date(),
      message: `${randomViewer} sent ${randomGift.emoji} to Player ${randomPlayer}`
    }]);
  };

  const startGame = () => {
    setGameStarted(true);
    setBoard(Array(9).fill(null));
    setIsXNext(true);
    setPlayerGifts({ X: 999, O: 999 }); // Reset to unlimited gifts
    setWinner(null);
    setGameHistory([]);
    setSpecialActive(null);
    setShowWinnerModal(false);
  };

  const resetGame = () => {
    setShowWinnerModal(false);
    setGameStarted(false); // Go back to start screen instead of starting immediately
  };

  const renderSquare = (index) => {
    const isBlocked = board[index] === "BLOCKED";
    return (
      <button 
        className={`${styles.square} ${isBlocked ? styles.blocked : ''}`} 
        onClick={() => handleClick(index)}
        disabled={!gameStarted || winner || isBlocked}
      >
        {isBlocked ? '🚫' : (board[index] || '')}
      </button>
    );
  };

  const status = !gameStarted 
    ? "Click Start Game to begin!" 
    : winner 
      ? winner === 'Draw' 
        ? 'Game ended in a draw!' 
        : `Winner: ${winner}`
      : `Next player: ${isXNext ? 'X (Owner)' : 'O (Opponent)'}`;

  return (
    <Layout
      title="TikTok Toe - Play Tic-Tac-Toe with Gifts"
      description="Play Tic-Tac-Toe TikTok style! Send gifts to make moves and win the game."
    >
      <div className={styles.container}>
        <h1 className={styles.title}>TikTok Toe 🎵</h1>
        <p className={styles.subtitle}>Owner: Free Rose moves | Special powers require gifts</p>
        
        {specialActive && (
          <div className={styles.specialAlert}>
            {specialActive}
          </div>
        )}
        
        {showWinnerModal && (
          <div className={styles.modalOverlay}>
            <div className={styles.winnerModal}>
              <h2>{winner === 'Draw' ? "It's a Draw!" : `Player ${winner} Wins!`}</h2>
              <div className={styles.winnerCelebration}>
                {winner !== 'Draw' && (
                  <>
                    <div className={styles.confetti}></div>
                    <div className={styles.confetti}></div>
                    <div className={styles.confetti}></div>
                    <div className={styles.confetti}></div>
                    <div className={styles.confetti}></div>
                  </>
                )}
                <p>{winner === 'Draw' ? "The game ended in a draw." : `Congratulations Player ${winner}!`}</p>
              </div>
              <button className={styles.modalButton} onClick={resetGame}>
                Play Again
              </button>
            </div>
          </div>
        )}
        
        <div className={styles.gameContainer}>
          {!gameStarted ? (
            <div className={styles.startGameScreen}>
              <h2>Ready to Play TikTok Toe?</h2>
              <p>As the owner, Rose moves are free! Special powers require gifts.</p>
              <button className={styles.startButton} onClick={startGame}>
                Start Game
              </button>
            </div>
          ) : (
            <>
             
              
              <div className={styles.sidePanel}>
                <div className={styles.giftSection}>
                  <h3>Gift Legend</h3>
                  <div className={styles.giftOptions}>
                    {Object.values(GIFTS).map(gift => (
                      <div
                        key={gift.id}
                        className={`${styles.giftItem} ${selectedGift.id === gift.id ? styles.selected : ''}`}
                        onClick={() => setSelectedGift(gift)}
                      >
                        <span className={styles.giftEmoji}>{gift.emoji}</span>
                        <div className={styles.giftInfo}>
                          <span className={styles.giftName}>{gift.name}</span>
                          <span className={styles.giftCost}>
                            Cost: {gift.id === GIFTS.ROSE.id ? "Free for Owner" : gift.cost}
                          </span>
                          {gift.special && (
                            <span className={styles.giftSpecial}>Special: {gift.special.replace('_', ' ')}</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <div className={styles.ownerActions}>
                    <h4>Owner Actions</h4>
                    <button 
                      className={styles.sendGiftButton}
                      onClick={() => sendGiftToOpponent(selectedGift)}
                      disabled={isXNext}
                    >
                      Send {selectedGift.emoji} to Opponent
                    </button>
                    
                    <button 
                      className={styles.simulateGiftButton}
                      onClick={simulateRandomGift}
                    >
                      Simulate Viewer Gift
                    </button>
                  </div>
                </div>
                
                
              </div> <div className={styles.gameBoard}>
                
                <div className={styles.board}><div className={styles.status}>{status}</div>
                  <div className={styles.boardRow}>
                    {renderSquare(0)}
                    {renderSquare(1)}
                    {renderSquare(2)}
                  </div>
                  <div className={styles.boardRow}>
                    {renderSquare(3)}
                    {renderSquare(4)}
                    {renderSquare(5)}
                  </div>
                  <div className={styles.boardRow}>
                    {renderSquare(6)}
                    {renderSquare(7)}
                    {renderSquare(8)}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
        <div className={styles.infoRow}>
        <div className={styles.instructions}>
          <h3>How to Play</h3>
          <ol>
            <li>Owner (Player X) gets free Rose moves 🌹</li>
            <li>Special powers require gifts (even for owner)</li>
            <li>Owner can send gifts to opponent</li>
            <li>Viewers can send gifts to either player</li>
            <li>First to get three in a row wins!</li>
          </ol>
        </div>
        <div className={styles.infoRow}>
                  <div className={styles.scoreSection}>
                    <h3>Gift Count</h3>
                    <div className={styles.playerScores}>
                      <div className={styles.player}>
                        <span>Player X (Owner): </span>
                        <span className={styles.giftCount}>{playerGifts.X}</span>
                      </div>
                      <div className={styles.player}>
                        <span>Player O (Opponent): </span>
                        <span className={styles.giftCount}>{playerGifts.O}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className={styles.historySection}>
                    <h3>Activity History</h3>
                    <div className={styles.historyList}>
                      {gameHistory.slice().reverse().map((move, index) => (
                        <div key={index} className={styles.historyItem}>
                          {move.message ? (
                            <span>{move.message}</span>
                          ) : (
                            <>
                              <span>Player {move.player}</span>
                              <span>{move.gift.emoji}</span>
                              <span>Position {move.position + 1}</span>
                            </>
                          )}
                        </div>
                      ))}
                      {gameHistory.length === 0 && (
                        <p className={styles.noHistory}>No activity yet</p>
                      )}
                    </div>
                  </div>
                </div></div>
        <Link to="/" className={styles.backLink}>
          ← Back to Home
        </Link>
      </div>
    </Layout>
  );
};

export default TikTokToe;